import { memo, useMemo } from "react";

import { Box, Divider, Typography } from "@mui/material";

import { BarChart } from "@mui/x-charts/BarChart";

import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

import { useBszPlannerContext } from "../../context/bszPlannerContext";
import BszSectionCard from "../../../1_BSZFinderApp/components/sectionCards/BszSectionCard";
import { useAppContext } from "../../../../2_ksbHub/context/appContext";

import {
  CELL_GAP,
  CELL_SIZE,
} from "../myTimelineSection/constants/timelineConstants";
import { SCHOOL_YEAR_2026_2027 } from "../../mockData/schoolYearData";

dayjs.extend(isoWeek);

function PlannerClassWorkloadSection() {
  const { projects = [] } = useBszPlannerContext();
  const { isMobile } = useAppContext();

  const weeklyData = useMemo(() => {
    const workloadMap = {};

    /**
     * =========================================================
     * ✅ AGGREGATE PROJECT WORKLOAD
     * =========================================================
     */
    projects.forEach((project) => {
      if (!project.startDate || !project.dueDate || !project.estimatedMinutes) {
        return;
      }

      const start = dayjs(project.startDate);
      const end = dayjs(project.dueDate);

      const dayCount = end.diff(start, "day") + 1;

      const minutesPerDay = project.estimatedMinutes / dayCount;

      let current = start;

      while (current.isBefore(end) || current.isSame(end, "day")) {
        const monday = current.startOf("isoWeek");

        const key = monday.format("YYYY-MM-DD");

        workloadMap[key] = (workloadMap[key] || 0) + minutesPerDay;

        current = current.add(1, "day");
      }
    });

    /**
     * =========================================================
     * ✅ BUILD FULL SCHOOL YEAR
     * =========================================================
     */
    const schoolYearStart = dayjs(SCHOOL_YEAR_2026_2027.start).startOf(
      "isoWeek",
    );

    const schoolYearEnd = dayjs(SCHOOL_YEAR_2026_2027.end).endOf("isoWeek");

    const weeks = [];

    let current = schoolYearStart;

    while (
      current.isBefore(schoolYearEnd) ||
      current.isSame(schoolYearEnd, "day")
    ) {
      const key = current.format("YYYY-MM-DD");

      const weekStart = current;
      const weekEnd = current.add(6, "day");

      weeks.push({
        monday: key,

        week: weekStart.isoWeek(),

        label: `${weekStart.format("DD.MM")} - ${weekEnd.format("DD.MM")}`,

        range: `${weekStart.format("DD.MM.YYYY")} - ${weekEnd.format(
          "DD.MM.YYYY",
        )}`,

        minutes: Math.round(workloadMap[key] || 0),
      });

      current = current.add(1, "week");
    }

    return weeks;
  }, [projects]);

  const totalMinutes = projects.reduce(
    (sum, project) => sum + (project.estimatedMinutes || 0),
    0,
  );

  const highestWeek =
    weeklyData.length > 0
      ? weeklyData.reduce((max, current) =>
          current.minutes > max.minutes ? current : max,
        )
      : null;

  const chartWidth = weeklyData.length * (CELL_SIZE + CELL_GAP);

  return (
    <BszSectionCard
      title="Klassen-Workload"
      defaultExpanded={isMobile ? false : true}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 4,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Projekte
            </Typography>

            <Typography variant="h4">{projects.length}</Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Aufwand
            </Typography>

            <Typography variant="h4">
              {(totalMinutes / 60).toFixed(1)} h
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Spitzenwoche
            </Typography>

            <Typography variant="body1">
              {highestWeek ? highestWeek.label : "-"}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {weeklyData.length === 0 ? (
          <Typography sx={{ opacity: 0.6 }}>
            Noch keine Workload-Daten vorhanden.
          </Typography>
        ) : (
          <BarChart
            height={320}
            xAxis={[
              {
                scaleType: "band",
                data: weeklyData.map((w) => w.label),
                tickLabelStyle: {
                  angle: -30,
                  textAnchor: "end",
                  fontSize: 11,
                },
              },
            ]}
            series={[
              {
                data: weeklyData.map((w) =>
                  Number((w.minutes / 60).toFixed(1)),
                ),
                color: "#12B0AE",
                label: "Workload",
              },
            ]}
            slotProps={{
              legend: {
                hidden: true,
              },
            }}
          />
        )}
      </Box>
    </BszSectionCard>
  );
}

export default memo(PlannerClassWorkloadSection);
